import type {StrategyBackground} from '@api';
import {
    type EducationContentRequest,
    type FreelancerSignupAccountDraft,
    type FreelancerSignupProfileDraftCategories,
    type FreelancerSignupWorkflow,
    type FreelancerVertical,
    type FreelancingExperience,
    type FunctionalSkillResource,
    type GetRepatriationMetadataRequest,
    type InitWorkflowOperationRequest,
    type RepatriationMetadata,
    type SignupCompletion,
    type SignupCompletionField,
    type SignupContext,
    type UpdateCategoriesOperationRequest,
    type UpdateFreelancingExperienceOperationRequest,
    type UpdateOtherCategoryOperationRequest,
    type UpdateStrategySkillsRequest,
    type UpdateStrategySkillsRequestSkillsInner,
    UpdateStrategySkillsRequestSkillsInnerTypeEnum,
    type WorkExperience,
    type WorkExperienceContentRequest,
} from '@api';
import type {AccountCreationFormData} from '~/features/account/types/AccountCreationFormData';
import type {EditEducation} from '~/features/education/types/EditEducation';
import type {EditExperience} from '~/features/experience/types/EditExperience';
import {FreelancerSignupContext} from '~/features/freelancer-signup/_common/types/FreelancerSignupContext';
import type {SignupExperienceFormData} from '~/features/freelancer-signup/experience/types/SignupExperienceFormData';
import {FreelancerSignupStepName} from '~/features/freelancer-signup/navigation/types/FreelancerSignupStep';
import {
    type CompletionState,
    type Workflow,
    type WorkflowCreationRequest,
    type WorkflowRepatriationMetadata,
} from '~/features/freelancer-signup/workflow/types/Workflow';
import {FreelancingExperienceId} from '~/features/freelancing-experience/types/FreelancingExperience';
import type {Skill} from '~/features/skills/types/Skill';
import type {FreelancerSignupCategorization} from '../types/FreelancerSignupCategorization';

export const toApi = {
    educationContentRequest,
    freelancingExperienceRequest,
    initGetRepatriationMetadataRequest,
    initWorkflowOperationRequest,
    updateCategoriesOperationRequest,
    updateOtherCategoryOperationRequest,
    updateStrategySkillsRequest,
    workExperienceContentRequest,
};

export const toDomain = {
    account,
    categorization,
    categorizationOther,
    experiences,
    jobTitle,
    repatriationMetadata,
    strategyBackgrounds,
    topSkills,
    workflow,
};

/**
 * API Mappers
 */

function educationContentRequest(education: EditEducation): EducationContentRequest {
    return {
        degree: education.degree!,
        school: education.school!,
        graduationYear: education.graduationYear,
        current: education.current || false,
        description: education.description,
    };
}

function freelancingExperienceRequest(request: {freelancingExperience: FreelancingExperienceId}): UpdateFreelancingExperienceOperationRequest {
    return {
        updateFreelancingExperienceRequest: {
            freelancingExperience: _toFreelancingExperience(request.freelancingExperience),
        },
    };
}

function initGetRepatriationMetadataRequest(repatriationTokenId: string): GetRepatriationMetadataRequest {
    return {repatriationTokenId};
}

function initWorkflowOperationRequest(request: WorkflowCreationRequest): InitWorkflowOperationRequest {
    return {
        initWorkflowRequest: {
            freelancerVertical: _toFreelancerVertical(request.context),
            repatriationTokenId: request.repatriationTokenId,
            invitationCode: request.invitationCode,
            corporateProgram: request.corporateProgram,
            freelancingExperience: request.freelancingExperience ? _toFreelancingExperience(request.freelancingExperience) : undefined,
        },
    };
}

function updateCategoriesOperationRequest(categorization: FreelancerSignupCategorization): UpdateCategoriesOperationRequest {
    return {
        updateCategoriesRequest: {
            categories: categorization.categorizationSelection,
            preferredCategory: categorization.categorizationSelection[0],
            selectedByPredictions: categorization.isCategorisationSelectedByPrediction,
        },
    };
}

function updateOtherCategoryOperationRequest(categorizationOther: string): UpdateOtherCategoryOperationRequest {
    return {
        updateOtherCategoryRequest: {
            otherCategory: categorizationOther,
        },
    };
}

function updateStrategySkillsRequest(
    selectedFunctionalSkills: FunctionalSkillResource[],
    selectedGlobalSkills: Skill[],
): UpdateStrategySkillsRequest {
    const fctSkills: UpdateStrategySkillsRequestSkillsInner[] = selectedFunctionalSkills.map((skill) => ({
        idOrName: skill.humanId,
        type: UpdateStrategySkillsRequestSkillsInnerTypeEnum.Functional,
    }));
    const globalSkills: UpdateStrategySkillsRequestSkillsInner[] = selectedGlobalSkills.map((skill) => ({
        idOrName: skill.label,
        type: UpdateStrategySkillsRequestSkillsInnerTypeEnum.Global,
    }));
    return {
        skills: [...fctSkills, ...globalSkills],
    };
}

function workExperienceContentRequest(experience: EditExperience, imported: boolean): WorkExperienceContentRequest {
    return {
        description: experience.description!,
        title: experience.title!,
        clientCompanyName: experience.company!,
        clientCompanyId: experience.companyId,
        currentJob: experience.currentJob || false,
        activityArea: experience.area,
        startDate: experience.startDate!,
        endDate: experience.endDate,
        location: experience.location,
        asFreelancer: experience.asFreelancer || false,
        imported,
    };
}

function _toFreelancerVertical(context: FreelancerSignupContext): FreelancerVertical {
    switch (context) {
        case FreelancerSignupContext.STRATEGY:
            return 'STRATEGY';
        case FreelancerSignupContext.OPEN:
        case FreelancerSignupContext.REGULAR:
        default:
            return 'REGULAR';
    }
}

function _toFreelancingExperience(freelancingExperience: FreelancingExperienceId): FreelancingExperience {
    switch (freelancingExperience) {
        case FreelancingExperienceId.STARTING:
            return 'NEW_FREELANCER';
        case FreelancingExperienceId.INTERMEDIATE:
            return 'FEW_JOBS_FREELANCER';
        case FreelancingExperienceId.EXPERT:
            return 'ESTABLISHED_FREELANCER';
    }
}

/**
 * Domain Mappers
 */
function categorization(categories: FreelancerSignupProfileDraftCategories | undefined): string[] {
    if (!categories) {
        return [];
    }

    return [categories.preferredCategory, ...categories.selectedCategories.filter((category) => category !== categories.preferredCategory)];
}

function categorizationOther(categorizationOther: string | undefined): string {
    return categorizationOther || '';
}

function jobTitle(headline: string | undefined) {
    return headline || '';
}

function repatriationMetadata(repatriationMetadata: RepatriationMetadata | undefined): WorkflowRepatriationMetadata | undefined {
    if (repatriationMetadata) {
        return {
            clientCompanyName: repatriationMetadata.clientCompanyName,
            clientCompanyLogoUrl: repatriationMetadata.clientCompanyLogoUrl,
            freelancerEmail: repatriationMetadata.freelancerEmail,
        };
    }
}

function strategyBackgrounds(strategyBackgrounds: Array<StrategyBackground> | undefined): Array<StrategyBackground> {
    return strategyBackgrounds || [];
}

function topSkills(globalTopSkills: Array<string> | undefined): Array<string> {
    return globalTopSkills || [];
}

function workflow(workflowResource: FreelancerSignupWorkflow): Workflow {
    return {
        id: workflowResource.id,
        context: _toContext(workflowResource.context),
        status: workflowResource.freelancerSignupWorkflowState,
        ignoredSteps: _toStepName(workflowResource.ignoreCompletionFields),
        completionState: _toCompletionState(workflowResource.freelancerSignupCompletion),
    };
}

function _toContext(context: SignupContext): FreelancerSignupContext {
    if (context.repatriationMetadata) {
        return FreelancerSignupContext.OPEN;
    } else if (context.freelancerVertical === 'STRATEGY') {
        return FreelancerSignupContext.STRATEGY;
    }
    return FreelancerSignupContext.REGULAR;
}

function _toStepName(ignoredFields: SignupCompletionField[]): FreelancerSignupStepName[] {
    return ignoredFields.map((field) => {
        switch (field) {
            case 'PHONE_NUMBER':
                return FreelancerSignupStepName.YOUR_PHONE_NUMBER;
            case 'PROFILE_PICTURE':
                return FreelancerSignupStepName.YOUR_PROFILE_PICTURE;
            default:
                throw new Error(`Unexpected ignored field ${field}`);
        }
    });
}

function _toCompletionState(completions: SignupCompletion[]): CompletionState {
    return completions.reduce(
        (result, completion) => ({
            [completion.step]: completion.completion,
            ...result,
        }),
        {} as CompletionState,
    );
}

function account(account: FreelancerSignupAccountDraft | undefined): AccountCreationFormData | undefined {
    if (account) {
        return {
            firstname: account.firstName,
            lastname: account.lastName,
            email: account.email,
            isCheckedMaltsPrivacyPolicy: account.areTermsAndConditionsAccepted,
            isCheckedMaltsTermsAndConditions: account.areTermsAndConditionsAccepted,
        };
    }
}

function experiences(experiences: WorkExperience[]): SignupExperienceFormData[] {
    return experiences.map((apiXp) => {
        return {
            id: apiXp.id,
            imported: apiXp.imported,
            title: apiXp.title,
            description: apiXp.description,
            clientCompanyName: apiXp.clientCompanyName,
            clientCompanyId: apiXp.clientCompanyId,
            clientCompanyOrganizationId: apiXp.clientCompanyOrganizationId,
            activityArea: apiXp.activityArea,
            startDate: apiXp.startDate,
            endDate: apiXp.endDate,
            location: apiXp.location,
            currentJob: apiXp.currentJob,
            asFreelancer: apiXp.asFreelancer,
        };
    });
}
